/**
 * ============================================================================
 * Input Sanitization & Validation Utilities
 * File: src/utils/inputSanitization.js
 * ============================================================================
 * 
 * Provides functions to sanitize and validate user input on the frontend
 * to prevent XSS, SQL injection, and other malicious input attempts.
 */

/**
 * List of dangerous patterns that should be blocked
 */
const DANGEROUS_PATTERNS = [
  /<script/i,
  /<\/script>/i,
  /javascript:/i,
  /onerror=/i,
  /onclick=/i,
  /onload=/i,
  /onmouseover=/i,
  /onfocus=/i,
  /onblur=/i,
  /<iframe/i,
  /<embed/i,
  /<object/i,
  /eval\(/i,
  /expression\(/i,
  /vbscript:/i,
  /data:text\/html/i,
  /--/,  // SQL comment
  /;.*drop/i,  // SQL injection attempt
  /;.*delete/i,  // SQL injection attempt
  /union.*select/i,  // SQL injection attempt
  /insert.*into/i,  // SQL injection attempt
];

/**
 * List of dangerous characters for specific input types
 */
const DANGEROUS_CHARS = {
  // For text inputs (names, clubs, etc)
  text: /[<>{}\\;]/g,
  
  // For email inputs (stricter)
  email: /[<>{}\\;'"]/g,
  
  // For numeric inputs
  number: /[^0-9.\-]/g,
  
  // For phone inputs
  phone: /[^0-9()\-+\s]/g,
  
  // For textarea (allow more, but still block scripts)
  textarea: /[<>{}\\]/g,
};

/**
 * Sanitize text input by removing dangerous characters
 * @param {string} value - The input value
 * @param {string} type - Type of input (text, email, number, phone, textarea)
 * @returns {string} - Sanitized value
 */
export const sanitizeInput = (value, type = 'text') => {
  if (!value) return '';
  
  let sanitized = String(value);
  
  // Remove dangerous characters based on type
  if (DANGEROUS_CHARS[type]) {
    sanitized = sanitized.replace(DANGEROUS_CHARS[type], '');
  }
  
  return sanitized;
};

/**
 * Check if input contains dangerous patterns
 * @param {string} value - The input value to check
 * @returns {boolean} - True if dangerous patterns found
 */
export const containsDangerousPatterns = (value) => {
  if (!value) return false;
  
  const stringValue = String(value).toLowerCase();
  
  return DANGEROUS_PATTERNS.some(pattern => pattern.test(stringValue));
};

/**
 * Validate and sanitize input
 * Returns an object with sanitized value and validation status
 * @param {string} value - The input value
 * @param {string} type - Type of input
 * @returns {object} - { valid: boolean, sanitized: string, error: string }
 */
export const validateAndSanitize = (value, type = 'text') => {
  if (!value) {
    return { valid: true, sanitized: '', error: null };
  }
  
  // Check for dangerous patterns first
  if (containsDangerousPatterns(value)) {
    return {
      valid: false,
      sanitized: '',
      error: 'Invalid characters detected. Please remove special characters.'
    };
  }
  
  // Sanitize based on type
  const sanitized = sanitizeInput(value, type);
  
  // Additional validation based on type
  switch (type) {
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(sanitized)) {
        return {
          valid: false,
          sanitized: sanitized,
          error: 'Please enter a valid email address'
        };
      }
      break;
      
    case 'phone':
      // Allow international formats
      if (sanitized.length < 10) {
        return {
          valid: false,
          sanitized: sanitized,
          error: 'Please enter a valid phone number'
        };
      }
      break;
      
    case 'number':
      if (isNaN(sanitized)) {
        return {
          valid: false,
          sanitized: '',
          error: 'Please enter a valid number'
        };
      }
      break;
  }
  
  return { valid: true, sanitized: sanitized.trim(), error: null };
};

/**
 * React onChange handler that sanitizes input in real-time
 * Usage: onChange={(e) => handleSanitizedChange(e, setMyState, 'text')}
 * @param {Event} event - The change event
 * @param {Function} setState - State setter function
 * @param {string} type - Type of input
 */
export const handleSanitizedChange = (event, setState, type = 'text') => {
  const { value } = event.target;
  const sanitized = sanitizeInput(value, type);
  setState(sanitized);
  
  // Update the input value if it was sanitized
  if (sanitized !== value) {
    event.target.value = sanitized;
  }
};

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} - True if valid
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number (supports multiple formats)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid
 */
export const isValidPhone = (phone) => {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  // Check if it has at least 10 digits
  return cleaned.length >= 10 && cleaned.length <= 15;
};

/**
 * Sanitize a complete form object
 * @param {object} formData - Object with form field values
 * @param {object} fieldTypes - Object mapping field names to types
 * @returns {object} - Sanitized form data
 * 
 * Example:
 * const sanitized = sanitizeFormData(
 *   { name: 'John<script>', email: 'test@test.com' },
 *   { name: 'text', email: 'email' }
 * );
 */
export const sanitizeFormData = (formData, fieldTypes = {}) => {
  const sanitized = {};
  
  Object.keys(formData).forEach(key => {
    const type = fieldTypes[key] || 'text';
    const result = validateAndSanitize(formData[key], type);
    sanitized[key] = result.sanitized;
  });
  
  return sanitized;
};

/**
 * Create a sanitized input component wrapper
 * This can be used to wrap any input component
 */
export const createSanitizedInput = (InputComponent, inputType = 'text') => {
  return ({ value, onChange, ...props }) => {
    const handleChange = (e) => {
      const sanitized = sanitizeInput(e.target.value, inputType);
      
      // Create a new event with sanitized value
      const sanitizedEvent = {
        ...e,
        target: {
          ...e.target,
          value: sanitized
        }
      };
      
      if (onChange) {
        onChange(sanitizedEvent);
      }
    };
    
    return <InputComponent {...props} value={value} onChange={handleChange} />;
  };
};

export default {
  sanitizeInput,
  containsDangerousPatterns,
  validateAndSanitize,
  handleSanitizedChange,
  isValidEmail,
  isValidPhone,
  sanitizeFormData,
  createSanitizedInput
};